import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Upload, RefreshCw, Trash2, Download, Plus, Edit, Check, X, LogOut,
  BookOpen, Layers, BarChart2, FileText, Clock, List, PlayCircle, ChevronDown, ChevronUp, AlertCircle, CheckCircle,
  Users, Bell
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem("lumis_admin_token")}` });

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("pdfs");
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    axios.get(`${API}/admin/me`, { headers: hdrs() }).catch(() => {
      localStorage.removeItem("lumis_admin_token");
      navigate("/admin", { replace: true });
    });
  }, [navigate]);

  // Poll unread count every 30s
  const fetchUnread = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/notifications/unread-count`, { headers: hdrs() });
      setUnreadCount(r.data.unread_count || 0);
    } catch {}
  }, []);

  useEffect(() => {
    fetchUnread();
    const iv = setInterval(fetchUnread, 30000);
    return () => clearInterval(iv);
  }, [fetchUnread]);

  const handleTabChange = (id) => {
    setTab(id);
    if (id === "relatorios" && unreadCount > 0) {
      axios.post(`${API}/admin/notifications/mark-read`, {}, { headers: hdrs() })
        .then(() => setUnreadCount(0)).catch(() => {});
    }
  };

  const TABS = [
    { id: "pdfs", label: "PDFs", icon: FileText },
    { id: "fila", label: "Fila", icon: List },
    { id: "relatorios", label: "Relatórios", icon: Clock, badge: unreadCount },
    { id: "estante", label: "Estante", icon: BookOpen },
    { id: "modulos", label: "Módulos", icon: Layers },
    { id: "votacao", label: "Votação", icon: BarChart2 },
    { id: "debug", label: "Debug RAG", icon: AlertCircle },
    { id: "usuarios", label: "Usuários", icon: Users },
    { id: "notificacoes", label: "Notificações", icon: Bell },
  ];

  return (
    <div className="min-h-screen bg-[#EEF2FF] flex flex-col" data-testid="admin-dashboard">
      <div className="bg-[#EEF2FF]/95 backdrop-blur-xl border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <span className="text-[#1E3A8A] font-outfit text-xl">✦</span>
          <div>
            <h1 className="text-gray-900 font-outfit font-light text-lg">Lumis Admin</h1>
            <p className="text-gray-500 text-xs font-manrope">Painel de Administração</p>
          </div>
        </div>
        <button
          data-testid="admin-logout-btn"
          onClick={() => { localStorage.removeItem("lumis_admin_token"); navigate("/admin"); }}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-900 text-sm font-manrope transition-all"
        >
          <LogOut size={16} /><span className="hidden sm:inline">Sair</span>
        </button>
      </div>

      <div className="bg-[#EEF2FF] border-b border-gray-200 px-2 flex gap-0 sticky top-[65px] z-10 overflow-x-auto">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => handleTabChange(t.id)}
              className={`relative flex items-center gap-1.5 px-3 py-3.5 text-xs font-manrope border-b-2 transition-all shrink-0 ${
                tab === t.id ? "border-[#1E3A8A] text-[#1E3A8A]" : "border-transparent text-gray-500 hover:text-gray-900"
              }`}
            >
              <Icon size={14} />
              <span className="hidden sm:inline">{t.label}</span>
              {t.badge > 0 && (
                <span data-testid={`tab-badge-${t.id}`} className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#1E3A8A] text-white rounded-full text-[9px] flex items-center justify-center font-bold">
                  {t.badge > 9 ? "9+" : t.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex-1 p-5 md:p-8 max-w-4xl mx-auto w-full">
        {tab === "pdfs" && <PDFTab />}
        {tab === "fila" && <FilaTab />}
        {tab === "relatorios" && <RelatoriosTab />}
        {tab === "estante" && <EstandeTab />}
        {tab === "modulos" && <ModulosTab />}
        {tab === "votacao" && <VotacaoTab />}
        {tab === "debug" && <DebugRAGTab />}
        {tab === "usuarios" && <UsuariosTab />}
        {tab === "notificacoes" && <NotificacoesTab />}
      </div>
    </div>
  );
}

/* ─── TAB: Debug RAG ─── */
function DebugRAGTab() {
  const [traces, setTraces] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/rag-traces?limit=50`, { headers: hdrs() });
      setTraces(r.data.traces || []);
      setTotal(r.data.total || 0);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const clearAll = async () => {
    if (!window.confirm("Apagar todos os traces de debug? Esta ação não pode ser desfeita.")) return;
    try {
      await axios.delete(`${API}/admin/rag-traces`, { headers: hdrs() });
      await load();
    } catch {}
  };

  const phaseColor = (phase) => {
    if (phase === "abertura") return "bg-blue-50 text-blue-700 border-blue-200";
    if (phase === "desenvolvimento") return "bg-[#DBEAFE] text-[#1E3A8A] border-[#1E3A8A]/20";
    if (phase === "encerramento") return "bg-emerald-50 text-emerald-700 border-emerald-200";
    return "bg-gray-100 text-gray-500 border-gray-300";
  };

  return (
    <div className="space-y-5" data-testid="admin-debug-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-gray-900 text-xl font-outfit font-light">Debug do Cérebro (RAG)</h2>
          <p className="text-gray-500 text-sm font-manrope mt-1">
            Auditoria em tempo real: gavetas consultadas, gatilhos casados, fase do ciclo socrático e instrução do turno.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="debug-refresh-btn"
            onClick={load}
            disabled={loading}
            className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-manrope px-3 py-2 rounded-lg border border-gray-300 transition-all"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Atualizar
          </button>
          <button
            data-testid="debug-clear-btn"
            onClick={clearAll}
            className="flex items-center gap-1.5 bg-red-50 hover:bg-red-100 text-red-700 text-sm font-manrope px-3 py-2 rounded-lg border border-red-200 transition-all"
          >
            <Trash2 size={14} /> Limpar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
          <p className="text-gray-500 text-xs font-manrope">Mensagens auditadas</p>
          <p className="text-gray-900 text-2xl font-outfit font-light mt-1">{total}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
          <p className="text-gray-500 text-xs font-manrope">Mostrando</p>
          <p className="text-gray-900 text-2xl font-outfit font-light mt-1">{traces.length}</p>
        </div>
      </div>

      {traces.length === 0 && !loading && (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
          <AlertCircle className="text-gray-500 mx-auto mb-2" size={28} />
          <p className="text-gray-500 text-sm font-manrope">Nenhum trace ainda. Envie uma mensagem no chat para gerar.</p>
        </div>
      )}

      <div className="space-y-3">
        {traces.map((t) => {
          const isOpen = expanded[t.trace_id];
          const d = t.debug || {};
          return (
            <div
              key={t.trace_id}
              data-testid={`trace-${t.trace_id}`}
              className="bg-white border border-gray-200 rounded-xl overflow-hidden"
            >
              <button
                data-testid={`trace-toggle-${t.trace_id}`}
                onClick={() => setExpanded((p) => ({ ...p, [t.trace_id]: !p[t.trace_id] }))}
                className="w-full px-4 py-3 flex items-start gap-3 text-left hover:bg-gray-50 transition-all"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <span className={`text-[10px] font-manrope px-2 py-0.5 rounded-full border ${phaseColor(d.phase)}`}>
                      {d.phase || "?"}
                    </span>
                    <span className="text-[10px] font-manrope text-gray-500">
                      Turno {d.turn} • Ciclo {d.cycle_position}/5
                    </span>
                    <span className="text-[10px] font-manrope text-gray-500">{fmt(t.created_at)}</span>
                  </div>
                  <p className="text-gray-900 text-sm font-manrope truncate">
                    <span className="text-[#1E3A8A]">›</span> {t.user_message}
                  </p>
                  <div className="flex items-center gap-3 mt-1.5 text-[11px] font-manrope text-gray-500">
                    <span>📚 {d.gavetas_injected?.length || 0} gaveta(s)</span>
                    <span>🧩 {d.experiences_injected || 0} experiência(s)</span>
                    <span>📝 {d.context_length || 0} chars</span>
                  </div>
                </div>
                {isOpen ? <ChevronUp size={18} className="text-gray-500" /> : <ChevronDown size={18} className="text-gray-500" />}
              </button>

              {isOpen && (
                <div className="border-t border-gray-200 p-4 space-y-4 bg-[#EEF2FF]/50">
                  <div>
                    <h4 className="text-[#1E3A8A] text-xs font-outfit uppercase tracking-wider mb-1.5">Instrução do turno</h4>
                    <p className="text-gray-700 text-xs font-manrope leading-relaxed bg-gray-50 rounded-lg p-3 border border-gray-200">
                      {d.cycle_instruction || "—"}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-[#1E3A8A] text-xs font-outfit uppercase tracking-wider mb-1.5">
                      Gavetas consultadas ({d.gavetas_consulted?.length || 0})
                    </h4>
                    {d.gavetas_consulted?.length > 0 ? (
                      <div className="space-y-1.5">
                        {d.gavetas_consulted.map((g, i) => (
                          <div key={i} className="bg-gray-50 border border-gray-200 rounded-lg p-2.5">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-gray-900 text-xs font-manrope font-medium">{g.gaveta}</span>
                              <span className="text-[10px] font-manrope text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                                score {g.score}
                              </span>
                            </div>
                            {g.triggers_matched?.length > 0 && (
                              <div className="mt-1.5 flex flex-wrap gap-1">
                                {g.triggers_matched.map((tr, j) => (
                                  <span key={j} className="text-[10px] font-manrope text-[#1E3A8A] bg-[#DBEAFE] border border-[#1E3A8A]/20 px-1.5 py-0.5 rounded">
                                    {tr}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-xs font-manrope italic">Nenhuma gaveta casou — IA usou conhecimento geral.</p>
                    )}
                  </div>

                  <div>
                    <h4 className="text-[#1E3A8A] text-xs font-outfit uppercase tracking-wider mb-1.5">Resposta da IA</h4>
                    <p className="text-gray-700 text-xs font-manrope leading-relaxed whitespace-pre-line bg-gray-50 rounded-lg p-3 border border-gray-200 max-h-48 overflow-y-auto">
                      {t.ai_response}
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── TAB: PDFs ─── */
function PDFTab() {
  const [livros, setLivros] = useState([]);
  const [processados, setProcessados] = useState([]);
  const [view, setView] = useState("aguardando");
  const [uploading, setUploading] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [logs, setLogs] = useState([]);
  const [cataloging, setCataloging] = useState(null);
  const [jobDone, setJobDone] = useState(false);
  const [lastQueued, setLastQueued] = useState(null);
  const [destino, setDestino] = useState("estante");
  const [tambemPerguntas, setTambemPerguntas] = useState(false);
  const [modules, setModules] = useState([]);
  const [gavetas, setGavetas] = useState([]);
  const [moduleId, setModuleId] = useState("");
  const [gavetaChoice, setGavetaChoice] = useState(""); // "" | "__new__" | gaveta_id
  const [novaGavetaNome, setNovaGavetaNome] = useState("");
  const fileRef = useRef(null);
  const pollRef = useRef(null);
  const logEnd = useRef(null);

  useEffect(() => { load(); loadModules(); loadGavetas(); return () => clearInterval(pollRef.current); }, []);
  useEffect(() => { logEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [logs]);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/livros`, { headers: hdrs() });
      setLivros(r.data.aguardando || r.data.livros || []);
      setProcessados(r.data.processados || []);
    } catch {}
  };

  const loadModules = async () => {
    try {
      const r = await axios.get(`${API}/modules`, { headers: hdrs() });
      setModules((r.data.modules || []).filter(m => (m.categorias || []).length > 0));
    } catch {}
  };

  const loadGavetas = async () => {
    try {
      const r = await axios.get(`${API}/admin/gavetas`, { headers: hdrs() });
      setGavetas(r.data.gavetas || []);
    } catch {}
  };

  const selectedModule = modules.find(m => m.module_id === moduleId);
  const moduleCatsLower = (selectedModule?.categorias || []).map(c => c.toLowerCase());
  const gavetasDoModulo = selectedModule
    ? gavetas.filter(g => moduleCatsLower.includes((g.categoria_principal || "").toLowerCase())
        || (g.categorias_secundarias || []).some(c => moduleCatsLower.includes((c || "").toLowerCase())))
    : [];
  const gavetaSelecionada = gavetas.find(g => g.gaveta_id === gavetaChoice);

  const upload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true); setLastQueued(null);
    let queued = 0, failed = 0;
    const categoria = gavetaChoice === "__new__" ? novaGavetaNome.trim() : (gavetaSelecionada?.categoria_principal || "");
    const gavetaNome = gavetaChoice === "__new__" ? novaGavetaNome.trim() : (gavetaSelecionada?.nome_gaveta || "");
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f); fd.append("destino", destino);
      if (moduleId && categoria) {
        fd.append("categoria", categoria);
        fd.append("gaveta_nome", gavetaNome);
        fd.append("module_id", moduleId);
        fd.append("module_name", selectedModule?.name || "");
      }
      if (destino === "estante" && tambemPerguntas) fd.append("also_basedeperguntas", "true");
      try {
        const r = await axios.post(`${API}/admin/upload-pdf`, fd, { headers: { ...hdrs(), "Content-Type": "multipart/form-data" } });
        if (r.data.queued) queued++;
        if (r.data.queued_perguntas) queued++;
      } catch { failed++; }
    }
    if (failed > 0) alert(`${failed} arquivo(s) falharam no upload.`);
    setLastQueued(queued > 0 ? `${queued} arquivo(s) adicionado(s) à fila` : null);
    await load();
    setUploading(false); fileRef.current.value = "";
  };

  const catalog = async (filename) => {
    setCataloging(filename); setLogs([]); setJobDone(false);
    try {
      const r = await axios.post(`${API}/admin/catalog-pdf/${filename}`, {}, { headers: hdrs() });
      const id = r.data.job_id; setJobId(id);
      pollRef.current = setInterval(async () => {
        try {
          const s = await axios.get(`${API}/admin/catalog-status/${id}`, { headers: hdrs() });
          setLogs(s.data.log || []);
          if (s.data.status === "done" || s.data.status === "error") {
            clearInterval(pollRef.current); setJobDone(true);
            if (s.data.status === "done") { setCataloging(null); await load(); await loadGavetas(); }
          }
        } catch {}
      }, 1500);
    } catch (err) { alert("Erro: " + (err.response?.data?.detail || err.message)); setCataloging(null); }
  };

  const deleteLivro = async (fn) => {
    if (!window.confirm(`Deletar ${fn}?`)) return;
    try { await axios.delete(`${API}/admin/livros/${fn}`, { headers: hdrs() }); await load(); } catch {}
  };

  return (
    <div className="space-y-5 lumis-fade">
      <div className="flex items-center justify-between">
        <h2 className="text-gray-900 font-outfit font-light text-xl">Livros & Catalogação</h2>
        <input ref={fileRef} type="file" accept=".md" multiple onChange={upload} className="hidden" data-testid="pdf-file-input" />
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 bg-white border border-gray-300 rounded-full px-4 py-2 text-sm font-manrope">
            {[["estante", "📚 Estante"], ["basedeperguntas", "💬 Base de Perguntas"]].map(([val, label]) => (
              <label key={val} className="flex items-center gap-1.5 cursor-pointer select-none">
                <input
                  type="radio"
                  name="destino"
                  value={val}
                  checked={destino === val}
                  onChange={() => setDestino(val)}
                  className="accent-[#1E3A8A]"
                  data-testid={`destino-${val}`}
                />
                <span className={destino === val ? "text-gray-900" : "text-gray-500"}>{label}</span>
              </label>
            ))}
          </div>
          {destino === "estante" && (
            <label className="flex items-center gap-1.5 cursor-pointer select-none text-xs font-manrope text-gray-500">
              <input
                type="checkbox"
                checked={tambemPerguntas}
                onChange={e => setTambemPerguntas(e.target.checked)}
                className="accent-[#1E3A8A]"
                data-testid="tambem-perguntas-checkbox"
              />
              + Base de Perguntas também
            </label>
          )}
          <button
            data-testid="upload-pdf-btn"
            onClick={() => fileRef.current.click()}
            disabled={uploading}
            className="flex items-center gap-2 bg-[#1E3A8A] text-white rounded-full px-4 py-2.5 text-sm font-manrope hover:bg-[#172554] active:scale-[0.98] transition-all disabled:opacity-50"
          >
            <Upload size={14} />{uploading ? "Enviando..." : "Adicionar Livros (.md)"}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-gray-500 text-xs font-manrope">Vincular o próximo upload a um módulo (opcional — evita a IA escolher uma categoria/nome que não bate com o módulo):</span>
        <select
          data-testid="upload-module-select"
          value={moduleId}
          onChange={e => { setModuleId(e.target.value); setGavetaChoice(""); setNovaGavetaNome(""); }}
          className="bg-white border border-gray-300 rounded-full px-3 py-1.5 text-xs text-gray-900 font-manrope outline-none focus:border-[#1E3A8A]"
        >
          <option value="">Nenhum (categoria e nome automáticos)</option>
          {modules.map(m => <option key={m.module_id} value={m.module_id}>{m.name}</option>)}
        </select>
        {selectedModule && (
          <select
            data-testid="upload-gaveta-select"
            value={gavetaChoice}
            onChange={e => setGavetaChoice(e.target.value)}
            className="bg-white border border-gray-300 rounded-full px-3 py-1.5 text-xs text-gray-900 font-manrope outline-none focus:border-[#1E3A8A]"
          >
            <option value="">Selecione a gaveta...</option>
            {gavetasDoModulo.map(g => <option key={g.gaveta_id} value={g.gaveta_id}>{g.nome_gaveta}</option>)}
            <option value="__new__">+ Criar nova gaveta</option>
          </select>
        )}
        {gavetaChoice === "__new__" && (
          <input
            data-testid="upload-nova-gaveta-input"
            type="text"
            placeholder="Nome da nova gaveta"
            value={novaGavetaNome}
            onChange={e => setNovaGavetaNome(e.target.value)}
            className="bg-white border border-gray-300 rounded-full px-3 py-1.5 text-xs text-gray-900 font-manrope outline-none focus:border-[#1E3A8A] placeholder:text-gray-400"
          />
        )}
      </div>

      {lastQueued && (
        <div data-testid="queued-notice" className="bg-[#DBEAFE] border border-[#1E3A8A]/20 rounded-2xl px-4 py-3 flex items-center gap-2 lumis-fade">
          <Clock size={14} className="text-[#1E3A8A] shrink-0" />
          <p className="text-[#1E3A8A] text-sm font-manrope"><span className="font-medium">{lastQueued}</span> adicionado à fila de processamento automático.</p>
        </div>
      )}

      {/* Toggle entre Aguardando / Processados */}
      <div className="flex gap-2 bg-white border border-gray-200 rounded-full p-1 w-fit">
        <button
          data-testid="livros-tab-aguardando"
          onClick={() => setView("aguardando")}
          className={`px-4 py-1.5 rounded-full text-xs font-manrope transition-all ${view === "aguardando" ? "bg-[#1E3A8A] text-white" : "text-gray-500 hover:text-gray-900"}`}
        >
          Aguardando ({livros.length})
        </button>
        <button
          data-testid="livros-tab-processados"
          onClick={() => setView("processados")}
          className={`px-4 py-1.5 rounded-full text-xs font-manrope transition-all ${view === "processados" ? "bg-[#1E3A8A] text-white" : "text-gray-500 hover:text-gray-900"}`}
        >
          Processados ({processados.length})
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-3xl overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-200">
          <p className="text-gray-500 text-xs font-manrope uppercase tracking-widest">
            {view === "aguardando"
              ? `Em uploadLivros/ — aguardando processamento (${livros.length})`
              : `Em uploadLivros/processados/ — histórico (${processados.length})`}
          </p>
        </div>
        {view === "aguardando" ? (
          livros.length === 0 ? (
            <div className="px-5 py-10 text-center text-gray-500 text-sm font-manrope">Adicione um livro em .md para começar.</div>
          ) : (
            <div className="divide-y divide-gray-200">
              {livros.map(l => (
                <div key={l.filename} className="px-5 py-4 flex items-center gap-3" data-testid={`livro-row-${l.filename}`}>
                  <span className="text-[#1E3A8A] text-sm shrink-0">{l.type === "md" || l.type === "markdown" ? "📝" : "📄"}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 text-sm font-manrope truncate">{l.filename}</p>
                    <p className="text-gray-500 text-xs font-manrope">{(l.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      data-testid={`catalog-btn-${l.filename}`}
                      onClick={() => catalog(l.filename)}
                      disabled={!!cataloging}
                      className="flex items-center gap-1.5 bg-[#DBEAFE] text-[#1E3A8A] border border-[#1E3A8A]/20 rounded-full px-3 py-1.5 text-xs font-manrope hover:bg-[#1E3A8A]/25 transition-all disabled:opacity-50"
                    >
                      <RefreshCw size={11} className={cataloging === l.filename ? "animate-spin" : ""} />
                      Catalogar
                    </button>
                    <button data-testid={`delete-livro-${l.filename}`} onClick={() => deleteLivro(l.filename)} className="text-gray-500 hover:text-red-400 transition-all p-1.5">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          processados.length === 0 ? (
            <div className="px-5 py-10 text-center text-gray-500 text-sm font-manrope">Nenhum arquivo processado ainda.</div>
          ) : (
            <div className="divide-y divide-gray-200">
              {processados.map(l => (
                <div key={l.relative_path} className="px-5 py-3 flex items-center gap-3" data-testid={`processado-row-${l.filename}`}>
                  <span className="text-emerald-400 text-sm shrink-0">{l.type === "md" || l.type === "markdown" ? "📝" : "📄"}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 text-sm font-manrope truncate">{l.filename}</p>
                    <p className="text-gray-500 text-xs font-manrope">📅 {l.processed_date} · {(l.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <span className="text-[10px] font-manrope px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700">Catalogado</span>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {(jobId || logs.length > 0) && (
        <div className="bg-[#0a0a14] border border-[#1E3A8A]/25 rounded-3xl overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-200 flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${jobDone ? "bg-green-400" : "bg-[#1E3A8A] animate-pulse"}`} />
            <p className="text-gray-500 text-xs font-manrope uppercase tracking-widest">Log de Processamento</p>
          </div>
          <div className="p-5 max-h-56 overflow-y-auto space-y-1 font-mono text-xs" data-testid="catalog-log">
            {logs.map((l, i) => (
              <div key={i} className="flex gap-2">
                <span className="text-[#1E3A8A]/40 shrink-0">[{new Date(l.time).toLocaleTimeString("pt-BR")}]</span>
                <span className={l.msg.startsWith("✓") ? "text-green-400" : l.msg.startsWith("✗") ? "text-red-400" : "text-gray-500"}>{l.msg}</span>
              </div>
            ))}
            <div ref={logEnd} />
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── TAB: FILA ─── */
function FilaTab() {
  const [queue, setQueue] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [processingCount, setProcessingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [schedCfg, setSchedCfg] = useState({ hour: 4, minute: 0, next_run: null, max_retries: 3, retry_interval_seconds: 120 });
  const [editingSched, setEditingSched] = useState(false);
  const [draftHour, setDraftHour] = useState("04");
  const [draftMinute, setDraftMinute] = useState("00");
  const pollRef = useRef(null);

  const loadScheduler = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/scheduler-config`, { headers: hdrs() });
      setSchedCfg(r.data);
      setDraftHour(String(r.data.hour).padStart(2, "0"));
      setDraftMinute(String(r.data.minute).padStart(2, "0"));
    } catch {}
  }, []);

  const saveScheduler = async () => {
    const h = parseInt(draftHour, 10); const m = parseInt(draftMinute, 10);
    if (isNaN(h) || isNaN(m) || h < 0 || h > 23 || m < 0 || m > 59) {
      alert("Hora inválida. Use 0-23 e 0-59."); return;
    }
    try {
      await axios.put(`${API}/admin/scheduler-config`, { hour: h, minute: m }, { headers: hdrs() });
      setEditingSched(false);
      await loadScheduler();
    } catch (e) {
      alert(e?.response?.data?.detail || "Erro ao salvar");
    }
  };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/queue`, { headers: hdrs() });
      setQueue(r.data.queue);
      setPendingCount(r.data.pending_count);
      setProcessingCount(r.data.processing_count);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    loadScheduler();
    pollRef.current = setInterval(load, 5000);
    return () => clearInterval(pollRef.current);
  }, [load, loadScheduler]);

  const processNow = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API}/admin/process-now`, {}, { headers: hdrs() });
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || "Erro ao iniciar processamento");
    } finally { setProcessing(false); }
  };

  const removeItem = async (queueId) => {
    try { await axios.delete(`${API}/admin/queue/${queueId}`, { headers: hdrs() }); await load(); } catch {}
  };

  const cleanOrphans = async () => {
    if (!window.confirm("Remover da fila todos os arquivos perdidos (que não estão mais em disco)?")) return;
    try {
      const r = await axios.post(`${API}/admin/queue/clean-orphans`, {}, { headers: hdrs() });
      alert(`${r.data.removed} item(ns) órfão(s) removido(s) da fila.`);
      await load();
    } catch {}
  };

  const retryItem = async (queueId) => {
    try {
      await axios.post(`${API}/admin/queue/${queueId}/retry`, {}, { headers: hdrs() });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Falha ao re-tentar");
    }
  };

  const statusColor = (s) => ({
    pending: "text-yellow-700 bg-yellow-50 border-yellow-200",
    processing: "text-blue-700 bg-blue-50 border-blue-200",
    done: "text-green-700 bg-green-50 border-green-200",
    failed: "text-red-700 bg-red-50 border-red-200"
  }[s] || "text-gray-500 bg-gray-100 border-gray-300");

  const statusLabel = (s) => ({ pending: "Aguardando", processing: "Processando...", done: "Concluído", failed: "Falhou" }[s] || s);

  return (
    <div className="space-y-5 lumis-fade">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-gray-900 font-outfit font-light text-xl">Fila de Processamento</h2>
          <p className="text-gray-500 text-xs font-manrope mt-0.5">Sistema 100% automático com 3 tentativas em caso de falha</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="clean-orphans-btn"
            onClick={cleanOrphans}
            className="flex items-center gap-1.5 bg-gray-100 text-gray-700 rounded-full px-3 py-2 text-xs font-manrope hover:bg-gray-200 transition-all"
            title="Remover entradas cujos arquivos não estão mais no disco"
          >
            <Trash2 size={12} /> Limpar órfãos
          </button>
          <button
            data-testid="process-now-btn"
            onClick={processNow}
            disabled={processing || pendingCount === 0 || processingCount > 0}
            className="flex items-center gap-2 bg-[#1E3A8A] text-white rounded-full px-4 py-2.5 text-sm font-manrope hover:bg-[#172554] active:scale-[0.98] transition-all disabled:opacity-40"
          >
            <PlayCircle size={14} className={processing ? "animate-spin" : ""} />
            {processingCount > 0 ? "Processando..." : "Processar Agora"}
          </button>
        </div>
      </div>

      {/* Card de configuração do agendamento */}
      <div data-testid="scheduler-card" className="bg-white border border-[#1E3A8A]/15 rounded-2xl p-4 space-y-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#DBEAFE] flex items-center justify-center text-[#1E3A8A] shrink-0">
            <Clock size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-gray-900 text-sm font-manrope">
              Processamento diário às <span className="text-[#1E3A8A] font-medium" data-testid="sched-current-time">{String(schedCfg.hour).padStart(2, "0")}:{String(schedCfg.minute).padStart(2, "0")}</span> <span className="text-gray-500">(Brasília)</span>
            </p>
            <p className="text-gray-500 text-xs font-manrope mt-0.5 truncate">
              {schedCfg.next_run ? `Próxima: ${fmt(schedCfg.next_run)}` : "—"} · 3 tentativas com 2 min de intervalo
            </p>
          </div>
          {!editingSched && (
            <button
              data-testid="edit-scheduler-btn"
              onClick={() => setEditingSched(true)}
              className="flex items-center gap-1.5 text-[#1E3A8A] text-xs font-manrope hover:text-gray-900 transition-all px-3 py-1.5 rounded-full bg-[#DBEAFE] border border-[#1E3A8A]/20 shrink-0"
            >
              ✎ Editar
            </button>
          )}
        </div>
        {editingSched && (
          <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-gray-200">
            <input
              type="number" min="0" max="23" value={draftHour}
              onChange={e => setDraftHour(e.target.value)}
              data-testid="sched-hour-input"
              className="w-14 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 text-center text-sm font-manrope py-1.5 outline-none focus:border-[#1E3A8A]"
            />
            <span className="text-gray-500">:</span>
            <input
              type="number" min="0" max="59" value={draftMinute}
              onChange={e => setDraftMinute(e.target.value)}
              data-testid="sched-minute-input"
              className="w-14 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 text-center text-sm font-manrope py-1.5 outline-none focus:border-[#1E3A8A]"
            />
            <button data-testid="sched-save-btn" onClick={saveScheduler} className="bg-[#1E3A8A] text-white text-xs font-manrope rounded-full px-3 py-1.5 hover:bg-[#172554] transition-all">
              Salvar
            </button>
            <button onClick={() => setEditingSched(false)} className="text-gray-500 text-xs font-manrope hover:text-gray-900 transition-all px-2">
              Cancelar
            </button>
          </div>
        )}
      </div>

      {/* Status cards */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Aguardando", val: pendingCount, color: "text-yellow-600" },
          { label: "Processando", val: processingCount, color: "text-blue-600" },
          { label: "Total na fila", val: queue.length, color: "text-[#1E3A8A]" }
        ].map((c, i) => (
          <div key={i} className="bg-white border border-gray-200 rounded-2xl p-4">
            <p className="text-gray-500 text-xs font-manrope">{c.label}</p>
            <p className={`text-2xl font-outfit font-light mt-1 ${c.color}`}>{c.val}</p>
          </div>
        ))}
      </div>

      {processingCount > 0 && (
        <div data-testid="processing-banner" className="bg-blue-50 border border-blue-200 rounded-2xl px-4 py-3 flex items-center gap-2">
          <RefreshCw size={14} className="text-blue-500 animate-spin shrink-0" />
          <p className="text-blue-700 text-sm font-manrope">Processamento em andamento. Esta tela atualiza automaticamente.</p>
        </div>
      )}

      {loading ? <div className="text-center py-12 text-[#1E3A8A] text-2xl animate-pulse">✦</div> : (
        <div className="bg-white border border-gray-200 rounded-3xl overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-200">
            <p className="text-gray-500 text-xs font-manrope uppercase tracking-widest">Todos os itens ({queue.length})</p>
          </div>
          {queue.length === 0 ? (
            <div className="px-5 py-10 text-center text-gray-500 text-sm font-manrope">
              Fila vazia. Faça upload de um PDF para começar.
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {queue.map(item => (
                <div key={item.queue_id} data-testid={`queue-item-${item.queue_id}`} className="px-5 py-4 flex items-start gap-3">
                  <div className="flex-1 min-w-0 space-y-1">
                    <p className="text-gray-900 text-sm font-manrope truncate">{item.filename}</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs px-2 py-0.5 rounded-full border font-manrope ${statusColor(item.status)}`}>
                        {statusLabel(item.status)}
                      </span>
                      {item.gaveta_name && <span className="text-gray-500 text-xs font-manrope">→ {item.gaveta_name}</span>}
                      {item.module_name && <span className="text-[#1E3A8A] text-xs font-manrope" title={`Categoria travada: ${item.forced_categoria}`}>🔒 {item.module_name}</span>}
                      {item.error && <span className="text-red-600 text-xs font-manrope truncate max-w-[200px]" title={item.error}>⚠ {item.error}</span>}
                    </div>
                    <p className="text-gray-500 text-xs font-manrope">
                      Adicionado: {fmt(item.added_at)}
                      {item.processed_at && ` · Processado: ${fmt(item.processed_at)}`}
                    </p>
                  </div>
                  {item.status === "pending" && (
                    <button
                      data-testid={`remove-queue-${item.queue_id}`}
                      onClick={() => removeItem(item.queue_id)}
                      className="text-gray-500 hover:text-red-400 transition-all p-1.5 shrink-0"
                      title="Remover da fila"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                  {item.status === "failed" && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        data-testid={`retry-queue-${item.queue_id}`}
                        onClick={() => retryItem(item.queue_id)}
                        className="text-[#1E3A8A] hover:text-gray-900 transition-all p-1.5"
                        title="Re-tentar processamento"
                      >
                        <RefreshCw size={14} />
                      </button>
                      <button
                        data-testid={`remove-queue-${item.queue_id}`}
                        onClick={() => removeItem(item.queue_id)}
                        className="text-gray-500 hover:text-red-400 transition-all p-1.5"
                        title="Remover da fila"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── TAB: RELATÓRIOS ─── */
function RelatoriosTab() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try { const r = await axios.get(`${API}/admin/reports`, { headers: hdrs() }); setReports(r.data.reports); }
      catch {} finally { setLoading(false); }
    };
    load();
  }, []);

  const dur = (s) => {
    if (s < 60) return `${Math.round(s)}s`;
    return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
  };

  return (
    <div className="space-y-5 lumis-fade">
      <div>
        <h2 className="text-gray-900 font-outfit font-light text-xl">Histórico de Relatórios</h2>
        <p className="text-gray-500 text-xs font-manrope mt-0.5">Gerado automaticamente após cada processamento noturno</p>
      </div>

      {loading ? <div className="text-center py-12 text-[#1E3A8A] text-2xl animate-pulse">✦</div> :
        reports.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-3xl px-6 py-12 text-center space-y-2">
            <p className="text-gray-500 text-sm font-manrope">Nenhum relatório ainda.</p>
            <p className="text-gray-500 text-xs font-manrope">Os relatórios são gerados automaticamente após o processamento noturno (04:00h) ou manual.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reports.map(r => {
              const isExp = expanded === r.report_id;
              const hasFailures = r.books_failed?.length > 0;
              return (
                <div key={r.report_id} data-testid={`report-row-${r.report_id}`} className={`bg-white border rounded-3xl overflow-hidden transition-all ${!r.is_read ? "border-[#1E3A8A]/20" : "border-gray-200"}`}>
                  <div
                    className="px-5 py-4 flex items-start gap-3 cursor-pointer hover:bg-gray-50 transition-all"
                    onClick={() => setExpanded(isExp ? null : r.report_id)}
                  >
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${hasFailures ? "bg-yellow-50" : "bg-green-50"}`}>
                      {hasFailures ? <AlertCircle size={15} className="text-yellow-600" /> : <CheckCircle size={15} className="text-green-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-gray-900 text-sm font-outfit font-medium">
                          {fmt(r.started_at)}
                        </p>
                        <span className={`text-xs px-2 py-0.5 rounded-full border font-manrope ${r.triggered_by === "manual" ? "text-[#1E3A8A] bg-[#DBEAFE] border-[#1E3A8A]/20" : "text-gray-500 bg-gray-100 border-gray-300"}`}>
                          {r.triggered_by === "manual" ? "Manual" : "Automático"}
                        </span>
                        {!r.is_read && <span className="text-xs px-2 py-0.5 rounded-full bg-[#1E3A8A] text-white font-manrope font-medium">Novo</span>}
                      </div>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <p className="text-gray-500 text-xs font-manrope">{r.books_processed?.length || 0} livros · {r.total_insights} insights · {r.total_questions} perguntas · {r.total_triggers} gatilhos</p>
                        <p className="text-gray-500 text-xs font-manrope">{dur(r.duration_seconds)}</p>
                      </div>
                      {hasFailures && <p className="text-yellow-700 text-xs font-manrope mt-1">{r.books_failed.length} falha(s)</p>}
                    </div>
                    {isExp ? <ChevronUp size={16} className="text-gray-500 shrink-0 mt-1" /> : <ChevronDown size={16} className="text-gray-500 shrink-0 mt-1" />}
                  </div>

                  {isExp && (
                    <div className="px-5 pb-5 pt-3 border-t border-gray-200 space-y-4">
                      {/* Stats */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {[
                          { label: "Estante antes", val: r.gavetas_count_before },
                          { label: "Estante depois", val: r.gavetas_count_after },
                          { label: "Livros OK", val: r.books_processed?.length || 0 },
                          { label: "Livros c/ falha", val: r.books_failed?.length || 0 }
                        ].map((c, i) => (
                          <div key={i} className="bg-[#EEF2FF] rounded-xl p-3">
                            <p className="text-gray-500 text-xs font-manrope">{c.label}</p>
                            <p className="text-gray-900 text-lg font-outfit font-light">{c.val}</p>
                          </div>
                        ))}
                      </div>

                      {/* Livros processados */}
                      {r.books_processed?.length > 0 && (
                        <div>
                          <p className="text-gray-500 text-xs font-manrope uppercase tracking-widest mb-2">Livros Processados com Sucesso</p>
                          <div className="space-y-2">
                            {r.books_processed.map((b, i) => (
                              <div key={i} data-testid={`report-book-${i}`} className="bg-[#EEF2FF] rounded-xl px-4 py-3 flex items-start gap-3">
                                <CheckCircle size={14} className="text-green-600 shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-gray-900 text-xs font-manrope font-medium truncate">{b.filename}</p>
                                  <p className="text-gray-500 text-xs font-manrope">{b.gaveta_name}</p>
                                  <p className="text-gray-500 text-xs font-manrope">{b.insights_count} insights · {b.questions_count} perguntas · {b.triggers_count} gatilhos · {dur(b.duration_seconds)}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Livros com falha */}
                      {r.books_failed?.length > 0 && (
                        <div>
                          <p className="text-gray-500 text-xs font-manrope uppercase tracking-widest mb-2">Livros com Falha</p>
                          <div className="space-y-2">
                            {r.books_failed.map((b, i) => (
                              <div key={i} className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-start gap-3">
                                <AlertCircle size={14} className="text-red-500 shrink-0 mt-0.5" />
                                <div>
                                  <p className="text-gray-900 text-xs font-manrope font-medium">{b.filename}</p>
                                  <p className="text-red-600 text-xs font-manrope">{b.error}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      }
    </div>
  );
}

/* ─── TAB: ESTANTE ─── */
function EstandeTab() {
  const [gavetas, setGavetas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/admin/gavetas`, { headers: hdrs() }); setGavetas(r.data.gavetas); }
    catch {} finally { setLoading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Deletar esta gaveta?")) return;
    try { await axios.delete(`${API}/admin/gavetas/${id}`, { headers: hdrs() }); await load(); } catch {}
  };

  return (
    <div className="space-y-4 lumis-fade">
      <div className="flex items-center justify-between">
        <h2 className="text-gray-900 font-outfit font-light text-xl">Estante de Gavetas</h2>
        <span className="text-gray-500 text-sm font-manrope">{gavetas.length} gavetas</span>
      </div>
      {loading ? <div className="text-center py-12 text-[#1E3A8A] text-2xl animate-pulse">✦</div> :
        gavetas.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-3xl px-6 py-12 text-center text-gray-500 text-sm font-manrope">Nenhuma gaveta ainda.</div>
        ) : (
          <div className="space-y-2">
            {gavetas.map(g => (
              <div key={g.gaveta_id} className="bg-white border border-gray-200 rounded-3xl overflow-hidden" data-testid={`gaveta-row-${g.gaveta_id}`}>
                <div className="px-5 py-4 flex items-center gap-3 cursor-pointer hover:bg-gray-50 transition-all" onClick={() => setExpanded(expanded === g.gaveta_id ? null : g.gaveta_id)}>
                  <div className="w-8 h-8 rounded-xl bg-[#DBEAFE] flex items-center justify-center text-[#1E3A8A] text-sm shrink-0">✦</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 text-sm font-outfit font-medium truncate">{g.nome_gaveta}</p>
                    <p className="text-gray-500 text-xs font-manrope">{g.categoria_principal} · {g.insights_principais?.length || 0} insights · {g.perguntas_poderosas?.length || 0} perguntas</p>
                    {g.arquivo_origem && <p className="text-[#1E3A8A]/60 text-xs font-manrope truncate mt-0.5">📄 {g.arquivo_origem}</p>}
                  </div>
                  <button data-testid={`delete-gaveta-${g.gaveta_id}`} onClick={e => { e.stopPropagation(); del(g.gaveta_id); }} className="text-gray-500 hover:text-red-400 transition-all p-1.5">
                    <Trash2 size={14} />
                  </button>
                </div>
                {expanded === g.gaveta_id && (
                  <div className="px-5 pb-5 pt-3 border-t border-gray-200 space-y-4">
                    {g.instrucao_para_lumis && (
                      <div>
                        <p className="text-gray-500 text-xs uppercase tracking-widest font-manrope mb-1.5">Instrução para o Lumis</p>
                        <p className="text-gray-500 text-xs font-manrope leading-relaxed">{g.instrucao_para_lumis}</p>
                      </div>
                    )}
                    {g.perguntas_poderosas?.length > 0 && (
                      <div>
                        <p className="text-gray-500 text-xs uppercase tracking-widest font-manrope mb-1.5">Perguntas Poderosas ({g.perguntas_poderosas.length})</p>
                        <div className="space-y-1">{g.perguntas_poderosas.slice(0, 5).map((p, i) => <p key={i} className="text-gray-500 text-xs font-manrope">• {p}</p>)}</div>
                      </div>
                    )}
                    {g.gatilhos_de_busca?.length > 0 && (
                      <div>
                        <p className="text-gray-500 text-xs uppercase tracking-widest font-manrope mb-1.5">Gatilhos de Busca</p>
                        <div className="flex flex-wrap gap-1.5">{g.gatilhos_de_busca.map((t, i) => <span key={i} className="bg-[#DBEAFE] text-[#1E3A8A] text-xs font-manrope px-2 py-0.5 rounded-full border border-[#1E3A8A]/15">{t}</span>)}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

/* ─── TAB: MÓDULOS ─── */
function ModulosTab() {
  const [modules, setModules] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editMod, setEditMod] = useState(null);
  const [newM, setNewM] = useState({ name: "", description: "", icon: "Star", status: "locked", order: 10, categoriasInput: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => { try { const r = await axios.get(`${API}/modules`, { headers: hdrs() }); setModules(r.data.modules); } catch {} };

  const toggle = async (mod) => {
    const ns = mod.status === "active" ? "locked" : "active";
    try { await axios.put(`${API}/modules/${mod.module_id}`, { status: ns }, { headers: hdrs() }); await load(); } catch {}
  };
  const parseCategorias = (str) => (str || "").split(",").map(s => s.trim()).filter(Boolean);
  const addModule = async () => {
    setSaving(true);
    try {
      const { categoriasInput, ...rest } = newM;
      await axios.post(`${API}/modules`, { ...rest, categorias: parseCategorias(categoriasInput) }, { headers: hdrs() });
      setShowAdd(false);
      setNewM({ name: "", description: "", icon: "Star", status: "locked", order: 10, categoriasInput: "" });
      await load();
    }
    catch {} finally { setSaving(false); }
  };
  const saveEdit = async () => {
    setSaving(true);
    try {
      const { categoriasInput, ...rest } = editMod;
      await axios.put(`${API}/modules/${editMod.module_id}`, { ...rest, categorias: parseCategorias(categoriasInput) }, { headers: hdrs() });
      setEditMod(null); await load();
    }
    catch {} finally { setSaving(false); }
  };
  const delMod = async (id) => {
    if (!window.confirm("Deletar este módulo?")) return;
    try { await axios.delete(`${API}/modules/${id}`, { headers: hdrs() }); await load(); } catch {}
  };

  const SC = { active: "text-green-700 bg-green-50 border-green-200", locked: "text-gray-500 bg-gray-100 border-gray-300", development: "text-yellow-700 bg-yellow-50 border-yellow-200" };

  return (
    <div className="space-y-4 lumis-fade">
      <div className="flex items-center justify-between">
        <h2 className="text-gray-900 font-outfit font-light text-xl">Módulos Temáticos</h2>
        <button data-testid="add-module-btn" onClick={() => setShowAdd(!showAdd)} className="flex items-center gap-2 bg-[#1E3A8A] text-white rounded-full px-4 py-2.5 text-sm font-manrope hover:bg-[#172554] active:scale-[0.98] transition-all">
          <Plus size={14} /> Novo Módulo
        </button>
      </div>
      {showAdd && (
        <div className="bg-white border border-[#1E3A8A]/20 rounded-3xl p-5 space-y-3 lumis-fade">
          <h3 className="text-gray-900 font-outfit text-sm">Adicionar Módulo</h3>
          <input data-testid="new-module-name" value={newM.name} onChange={e => setNewM({ ...newM, name: e.target.value })} placeholder="Nome do módulo" className="w-full bg-[#EEF2FF] text-gray-900 border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none" />
          <textarea data-testid="new-module-desc" value={newM.description} onChange={e => setNewM({ ...newM, description: e.target.value })} placeholder="Descrição" rows={2} className="w-full bg-[#EEF2FF] text-gray-900 border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none resize-none" />
          <div className="flex gap-2">
            <select data-testid="new-module-status" value={newM.status} onChange={e => setNewM({ ...newM, status: e.target.value })} className="bg-[#EEF2FF] text-gray-900 border border-gray-300 rounded-xl px-3 py-2 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none flex-1">
              <option value="locked">Bloqueado</option><option value="active">Ativo</option><option value="development">Em desenvolvimento</option>
            </select>
            <input type="number" value={newM.order} onChange={e => setNewM({ ...newM, order: parseInt(e.target.value) || 10 })} placeholder="Ordem" className="w-20 bg-[#EEF2FF] text-gray-900 border border-gray-300 rounded-xl px-3 py-2 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none" />
          </div>
          <input
            data-testid="new-module-categorias"
            value={newM.categoriasInput}
            onChange={e => setNewM({ ...newM, categoriasInput: e.target.value })}
            placeholder="Categorias da estante (separadas por vírgula, ex: autoconhecimento, ansiedade-e-medo)"
            className="w-full bg-[#EEF2FF] text-gray-900 border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none"
          />
          <div className="flex gap-2">
            <button data-testid="save-new-module-btn" onClick={addModule} disabled={saving || !newM.name} className="flex items-center gap-2 bg-[#1E3A8A] text-white rounded-full px-4 py-2 text-sm font-manrope hover:bg-[#172554] disabled:opacity-50 transition-all"><Check size={13} /> Salvar</button>
            <button onClick={() => setShowAdd(false)} className="flex items-center gap-2 bg-gray-100 text-gray-500 rounded-full px-4 py-2 text-sm font-manrope hover:text-gray-900 transition-all"><X size={13} /> Cancelar</button>
          </div>
        </div>
      )}
      <div className="space-y-2">
        {modules.map(mod => (
          <div key={mod.module_id} className="bg-white border border-gray-200 rounded-2xl px-5 py-3.5 flex items-center gap-3" data-testid={`module-admin-row-${mod.module_id}`}>
            {editMod?.module_id === mod.module_id ? (
              <div className="flex-1 flex items-center gap-2 flex-wrap">
                <input value={editMod.name} onChange={e => setEditMod({ ...editMod, name: e.target.value })} className="bg-[#EEF2FF] text-gray-900 border border-gray-300 rounded-lg px-3 py-1.5 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none flex-1 min-w-24" />
                <input type="number" min="1" value={editMod.order ?? 10} onChange={e => setEditMod({ ...editMod, order: parseInt(e.target.value) || 1 })} className="bg-[#EEF2FF] text-gray-900 border border-gray-300 rounded-lg px-3 py-1.5 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none w-16 text-center" title="Posição" data-testid="edit-module-order" />
                <select value={editMod.status} onChange={e => setEditMod({ ...editMod, status: e.target.value })} className="bg-[#EEF2FF] text-gray-900 border border-gray-300 rounded-lg px-2 py-1.5 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none">
                  <option value="locked">Bloqueado</option><option value="active">Ativo</option><option value="development">Em dev</option>
                </select>
                <input
                  data-testid="edit-module-categorias"
                  value={editMod.categoriasInput}
                  onChange={e => setEditMod({ ...editMod, categoriasInput: e.target.value })}
                  placeholder="Categorias da estante (vírgula)"
                  className="bg-[#EEF2FF] text-gray-900 border border-gray-300 rounded-lg px-3 py-1.5 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none w-full"
                />
                <button onClick={saveEdit} disabled={saving} className="text-green-600 hover:text-green-500 p-1 transition-all"><Check size={16} /></button>
                <button onClick={() => setEditMod(null)} className="text-gray-500 hover:text-gray-900 p-1 transition-all"><X size={16} /></button>
              </div>
            ) : (
              <>
                <div className="flex-1 min-w-0 flex items-center gap-2">
                  <p className="text-gray-900 text-sm font-manrope truncate">{mod.name}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full border font-manrope shrink-0 ${SC[mod.status] || SC.locked}`}>{mod.status}</span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button data-testid={`toggle-status-btn-${mod.module_id}`} onClick={() => toggle(mod)} className={`text-xs px-3 py-1.5 rounded-full font-manrope transition-all ${mod.status === "active" ? "bg-gray-100 text-gray-500 hover:text-gray-900" : "bg-[#DBEAFE] text-[#1E3A8A] hover:bg-[#1E3A8A]/20"}`}>
                    {mod.status === "active" ? "Bloquear" : "Ativar"}
                  </button>
                  <button data-testid={`edit-module-btn-${mod.module_id}`} onClick={() => setEditMod({ ...mod, categoriasInput: (mod.categorias || []).join(", ") })} className="text-gray-500 hover:text-gray-900 transition-all p-1.5"><Edit size={13} /></button>
                  <button data-testid={`delete-module-btn-${mod.module_id}`} onClick={() => delMod(mod.module_id)} className="text-gray-500 hover:text-red-400 transition-all p-1.5"><Trash2 size={13} /></button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── TAB: VOTAÇÃO ─── */
function VotacaoTab() {
  const [votes, setVotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);
  const load = async () => { setLoading(true); try { const r = await axios.get(`${API}/votes`); setVotes(r.data.votes); } catch {} finally { setLoading(false); } };

  const exportCSV = async () => {
    try {
      const r = await axios.get(`${API}/admin/votes-export`, { headers: hdrs(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "lumis-votos.csv"; a.click();
    } catch {}
  };

  const locked = votes.filter(v => v.status !== "active");
  const chartData = locked.map(v => ({ name: v.name.split(" ")[0], votos: v.vote_count }));
  const total = votes.reduce((a, v) => a + v.vote_count, 0);

  return (
    <div className="space-y-5 lumis-fade">
      <div className="flex items-center justify-between">
        <h2 className="text-gray-900 font-outfit font-light text-xl">Painel de Votação</h2>
        <button data-testid="export-votes-btn" onClick={exportCSV} className="flex items-center gap-2 bg-white text-gray-500 border border-gray-300 rounded-full px-4 py-2.5 text-sm font-manrope hover:text-gray-900 transition-all active:scale-[0.98]">
          <Download size={14} /> Exportar CSV
        </button>
      </div>
      {loading ? <div className="text-center py-12 text-[#1E3A8A] text-2xl animate-pulse">✦</div> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { label: "Em votação", val: locked.length },
              { label: "Total de votos", val: total },
              { label: "Módulo líder", val: votes.sort((a, b) => b.vote_count - a.vote_count)[0]?.name?.split(" ")[0] || "—" }
            ].map((c, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-2xl p-4">
                <p className="text-gray-500 text-xs font-manrope">{c.label}</p>
                <p className="text-gray-900 text-2xl font-outfit font-light mt-1">{c.val}</p>
              </div>
            ))}
          </div>
          {chartData.length > 0 && chartData.some(d => d.votos > 0) && (
            <div className="bg-white border border-gray-200 rounded-3xl p-5">
              <p className="text-gray-500 text-xs font-manrope uppercase tracking-widest mb-4">Ranking Visual</p>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={chartData} margin={{ top: 0, right: 0, bottom: 0, left: -25 }}>
                  <XAxis dataKey="name" tick={{ fill: "#6B7280", fontSize: 11, fontFamily: "Manrope" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#6B7280", fontSize: 11, fontFamily: "Manrope" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid rgba(30,58,138,0.15)", borderRadius: 12, color: "#111827", fontFamily: "Manrope", fontSize: 12 }} cursor={{ fill: "rgba(30,58,138,0.08)" }} />
                  <Bar dataKey="votos" fill="#1E3A8A" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="bg-white border border-gray-200 rounded-3xl overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-200">
              <p className="text-gray-500 text-xs font-manrope uppercase tracking-widest">Ranking Detalhado</p>
            </div>
            <div className="divide-y divide-gray-200">
              {[...votes].sort((a, b) => b.vote_count - a.vote_count).map((v, i) => (
                <div key={v.module_id} className="px-5 py-3.5 flex items-center gap-3" data-testid={`vote-row-${v.module_id}`}>
                  <span className={`text-sm font-outfit font-light w-5 shrink-0 ${i === 0 ? "text-[#1E3A8A]" : "text-gray-400"}`}>#{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 text-sm font-manrope truncate">{v.name}</p>
                    <p className={`text-xs font-manrope ${v.status === "active" ? "text-green-600" : "text-gray-500"}`}>
                      {v.status === "active" ? "Ativo" : `${v.vote_count} votos`}
                    </p>
                  </div>
                  <p className="text-[#1E3A8A] text-sm font-outfit shrink-0">{v.vote_count}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ─── TAB: USUÁRIOS ─── */
function UsuariosTab() {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/admin/usuarios`, { headers: hdrs() }); setUsuarios(r.data.usuarios); }
    catch {} finally { setLoading(false); }
  };

  const toggleBloqueio = async (u) => {
    const action = u.bloqueado ? "ativar" : "bloquear";
    try { await axios.put(`${API}/admin/usuarios/${u.user_id}/${action}`, {}, { headers: hdrs() }); await load(); } catch {}
  };
  const toggleLiberacao = async (u) => {
    const msg = u.acesso_liberado
      ? `Tem certeza que deseja revogar a liberação de ${u.email}? Ele(a) voltará a precisar de assinatura após o trial.`
      : `Tem certeza que deseja liberar ${u.email}? Ele(a) poderá usar o sistema sem precisar assinar.`;
    if (!window.confirm(msg)) return;
    const action = u.acesso_liberado ? "revogar-liberacao" : "liberar";
    try { await axios.put(`${API}/admin/usuarios/${u.user_id}/${action}`, {}, { headers: hdrs() }); await load(); } catch {}
  };
  const excluir = async (u) => {
    if (!window.confirm(`Excluir a conta de ${u.email}? Isso também remove a Agenda dele(a). Não pode ser desfeito.`)) return;
    try { await axios.delete(`${API}/admin/usuarios/${u.user_id}`, { headers: hdrs() }); await load(); } catch {}
  };

  if (loading) return <p className="text-gray-500 text-sm font-manrope">Carregando...</p>;

  return (
    <div className="space-y-4 lumis-fade">
      <h2 className="text-gray-900 font-outfit font-light text-xl">Usuários Cadastrados</h2>
      {usuarios.length === 0 ? (
        <p className="text-gray-500 text-sm font-manrope">Nenhum usuário cadastrado ainda.</p>
      ) : (
        <div className="space-y-2">
          {usuarios.map(u => (
            <div key={u.user_id} className="bg-white border border-gray-200 rounded-2xl px-5 py-3.5 flex items-center gap-3 flex-wrap" data-testid={`user-row-${u.user_id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-gray-900 text-sm font-manrope truncate">{u.nome || u.email}</p>
                <p className="text-xs text-gray-500 font-manrope truncate">{u.email} · desde {fmt(u.created_at)}</p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full border font-manrope shrink-0 ${u.bloqueado ? "text-red-700 bg-red-50 border-red-200" : "text-green-700 bg-green-50 border-green-200"}`}>
                {u.bloqueado ? "Bloqueado" : "Ativo"}
              </span>
              <span className="text-xs px-2 py-0.5 rounded-full border font-manrope shrink-0 text-[#1E3A8A] bg-[#1E3A8A]/8 border-[#1E3A8A]/20">
                {u.subscription_status === "authorized"
                  ? "Assinante"
                  : u.acesso_liberado
                  ? "Liberado"
                  : u.trial_days_left > 0
                  ? `Trial: ${u.trial_days_left}d restantes`
                  : "Trial expirado"}
              </span>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  data-testid={`toggle-bloqueio-btn-${u.user_id}`}
                  onClick={() => toggleBloqueio(u)}
                  className={`text-xs px-3 py-1.5 rounded-full font-manrope transition-all ${u.bloqueado ? "bg-[#DBEAFE] text-[#1E3A8A] hover:bg-[#1E3A8A]/20" : "bg-gray-100 text-gray-500 hover:text-gray-900"}`}
                >
                  {u.bloqueado ? "Ativar" : "Bloquear"}
                </button>
                <button
                  data-testid={`toggle-liberacao-btn-${u.user_id}`}
                  onClick={() => toggleLiberacao(u)}
                  className={`text-xs px-3 py-1.5 rounded-full font-manrope transition-all ${u.acesso_liberado ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-gray-100 text-gray-500 hover:text-gray-900"}`}
                >
                  {u.acesso_liberado ? "Revogar liberação" : "Liberar"}
                </button>
                <button data-testid={`delete-user-btn-${u.user_id}`} onClick={() => excluir(u)} className="text-gray-500 hover:text-red-400 transition-all p-1.5">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── TAB: NOTIFICAÇÕES ─── */
function NotificacoesTab() {
  const [notifications, setNotifications] = useState([]);
  const [titulo, setTitulo] = useState("");
  const [mensagem, setMensagem] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/admin/user-notifications`, { headers: hdrs() }); setNotifications(r.data.notifications); }
    catch {} finally { setLoading(false); }
  };

  const enviar = async () => {
    if (!titulo.trim() || !mensagem.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/user-notifications`, { titulo, mensagem }, { headers: hdrs() });
      setTitulo(""); setMensagem("");
      await load();
    } catch {} finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 lumis-fade">
      <h2 className="text-gray-900 font-outfit font-light text-xl">Notificações para Usuários</h2>
      <div className="bg-white border border-[#1E3A8A]/20 rounded-3xl p-5 space-y-3">
        <h3 className="text-gray-900 font-outfit text-sm">Nova Notificação</h3>
        <input
          data-testid="new-notification-titulo"
          value={titulo}
          onChange={e => setTitulo(e.target.value)}
          placeholder="Título"
          className="w-full bg-[#EEF2FF] text-gray-900 border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none"
        />
        <textarea
          data-testid="new-notification-mensagem"
          value={mensagem}
          onChange={e => setMensagem(e.target.value)}
          placeholder="Mensagem"
          rows={3}
          className="w-full bg-[#EEF2FF] text-gray-900 border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-manrope focus:border-[#1E3A8A]/60 outline-none resize-none"
        />
        <button
          data-testid="send-notification-btn"
          onClick={enviar}
          disabled={saving || !titulo.trim() || !mensagem.trim()}
          className="flex items-center gap-2 bg-[#1E3A8A] text-white rounded-full px-4 py-2.5 text-sm font-manrope hover:bg-[#172554] disabled:opacity-50 transition-all"
        >
          <Bell size={14} /> Enviar
        </button>
      </div>

      <h3 className="text-gray-500 text-xs font-manrope uppercase tracking-widest">Histórico</h3>
      {loading ? (
        <p className="text-gray-500 text-sm font-manrope">Carregando...</p>
      ) : notifications.length === 0 ? (
        <p className="text-gray-500 text-sm font-manrope">Nenhuma notificação enviada ainda.</p>
      ) : (
        <div className="space-y-2">
          {notifications.map(n => (
            <div key={n.id} className="bg-white border border-gray-200 rounded-2xl px-5 py-3.5" data-testid={`notification-row-${n.id}`}>
              <div className="flex items-center justify-between gap-3">
                <p className="text-gray-900 text-sm font-manrope">{n.titulo}</p>
                <span className="text-xs text-gray-500 font-manrope shrink-0">{n.visualizacoes} visualização(ões)</span>
              </div>
              <p className="text-xs text-gray-500 font-manrope mt-1">{n.mensagem}</p>
              <p className="text-[10px] text-gray-500/60 font-manrope mt-1">{fmt(n.created_at)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
